import { getFieldResponsesId } from "../aggregate";
import { resolvePlanForImpact } from "../planForImpact";
import type { SideEffect } from ".";

/** What the resolver needs from a store: synchronous reads of the response
 * bodies it already holds. Every store's cache exposes exactly these. */
export type FieldResponsesLookup = {
  byVersion(proposalId: string, version: string): Record<string, string> | null;
  newest(proposalId: string): Record<string, string> | null;
};

/**
 * Turn the reducer's intent effects into the concrete writes a store applies,
 * leaving every other effect untouched and in order.
 *
 * This is where the reads the reducer used to do now happen — inside the info
 * store, the layer that owns the response bodies — so the aggregate stays a
 * pure function of the action log. Resolution mirrors the reducer's old logic
 * exactly (merge base by `beforeFieldResponsesId`, fall back to the newest
 * with a warning, warn on an after-id mismatch, derive planForImpact), so the
 * stores' contents come out the same as before.
 *
 * Effects within one batch are resolved in order against an overlay of the
 * writes already produced by that batch — every version, not just the newest,
 * so a later intent finds whichever earlier version it names (a stale edit
 * forking from an older version included), exactly as the reducer did when it
 * read a store holding all versions between actions.
 */
export function resolveIntentEffects(
  effects: SideEffect[],
  lookup: FieldResponsesLookup,
): SideEffect[] {
  // proposalId -> (version -> body) for every write this batch has produced,
  // plus the newest one per proposal.
  const pendingVersions = new Map<
    string,
    Map<string, Record<string, string>>
  >();
  const pendingNewest = new Map<string, Record<string, string>>();
  const remember = (proposalId: string, body: Record<string, string>) => {
    let versions = pendingVersions.get(proposalId);
    if (!versions) {
      versions = new Map();
      pendingVersions.set(proposalId, versions);
    }
    versions.set(getFieldResponsesId(body), body);
    pendingNewest.set(proposalId, body);
  };
  const byVersion = (proposalId: string, version: string) =>
    pendingVersions.get(proposalId)?.get(version) ??
    lookup.byVersion(proposalId, version);
  const newest = (proposalId: string) =>
    pendingNewest.get(proposalId) ?? lookup.newest(proposalId);

  const out: SideEffect[] = [];
  for (const effect of effects) {
    switch (effect.type) {
      case "PATCH_FIELD_RESPONSE": {
        const {
          roundId,
          proposalId,
          fieldId,
          response,
          beforeFieldResponsesId,
          afterFieldResponsesId,
          actionId,
          proposalInfo,
        } = effect;
        // Look up the exact version the client edited, rather than the
        // newest, which would be wrong when replaying.
        let base: Record<string, string> | null = null;
        if (beforeFieldResponsesId) {
          base = byVersion(proposalId, beforeFieldResponsesId);
          if (!base) {
            console.warn(
              `Action ${actionId}: Could not find field responses for beforeFieldResponsesId ${beforeFieldResponsesId}. ` +
                `Falling back to the newest field responses.`,
            );
            base = newest(proposalId);
          }
        }
        const fieldResponses = { ...base, [fieldId]: response };
        const expected = getFieldResponsesId(fieldResponses);
        if (expected !== afterFieldResponsesId) {
          console.warn(
            `Action ${actionId}: Field responses ID mismatch for proposal ${proposalId}: expected ${expected}, got ${afterFieldResponsesId}.`,
            "This indicates that you may not see the latest responses until they're fetched from the server.",
          );
        }
        remember(proposalId, fieldResponses);
        out.push({
          type: "WRITE_FIELD_RESPONSES",
          roundId,
          proposalId,
          fieldResponses,
        });
        if (proposalInfo) {
          out.push({
            type: "WRITE_PROPOSAL_INFO",
            roundId,
            ...proposalInfo.proposal,
            planForImpact: resolvePlanForImpact(
              proposalInfo.fields,
              fieldResponses,
            ),
          });
        }
        break;
      }
      case "REDERIVE_PROPOSAL_INFO": {
        for (const proposal of effect.proposals) {
          const fieldResponses = newest(proposal.proposalId);
          // No responses held for it: emitting with none would wrongly clear
          // planForImpact, so leave the stored value alone.
          if (!fieldResponses) continue;
          out.push({
            type: "WRITE_PROPOSAL_INFO",
            roundId: effect.roundId,
            ...proposal,
            planForImpact: resolvePlanForImpact(effect.fields, fieldResponses),
          });
        }
        break;
      }
      case "WRITE_FIELD_RESPONSES":
        remember(effect.proposalId, effect.fieldResponses);
        out.push(effect);
        break;
      default:
        out.push(effect);
    }
  }
  return out;
}
